import moment from 'moment-timezone';
import anchorme from 'anchorme';
import i18n from 'locale';
import prettyMs from 'pretty-ms';
import { stringToColor } from './str-to-color';

const formatUSAndInternationalNumber = (number) => {
  // Remove all non-numeric characters
  let cleaned = ('' + number).replace(/\D/g, '');

  // Check if the number has an international code (more than 10 digits)
  if (cleaned.length > 10) {
    // Remove the international code (assuming it's the first digits)
    cleaned = cleaned.slice(cleaned.length - 10);
  }

  // Format the cleaned number
  let formatted = cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

  return formatted;
};

export function isMobile() {
  return (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino|android|ipad|playbook|silk/i.test(
      navigator.userAgent || navigator.vendor || window.opera,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      (navigator.userAgent || navigator.vendor || window.opera).substr(0, 4),
    )
  );
}

export const getName = (contact) => {
  if (!contact) return 'Person Removed';
  const {
    contact_first_name,
    contact_last_name,
    contact_email,
    primary_phone,
  } = contact;
  if (!contact_first_name && !contact_last_name)
    return primary_phone || contact_email || 'Unknown';
  if (contact_first_name && contact_last_name)
    return contact_first_name + ' ' + contact_last_name;
  return contact_first_name || contact_last_name;
};

export const formatPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) return phoneNumberString;
  return formatUSAndInternationalNumber(phoneNumberString);
  // const formatted = formatPhoneNumberLocal(phoneNumberString);
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phoneNumberString;
};

export const formatAddressString = () => {};

export const isUrl = (s) => {
  const regexp =
    /^(https?):\/\/(www\.)?[a-zA-Z0-9-@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;
  return regexp.test(s);
};

export const withHttp = (url) =>
  !/^https?:\/\//i.test(url) ? `https://${url}` : url;

export function isValidEmail(text) {
  if (!text) return false;
  // From: https://stackoverflow.com/questions/46155/how-to-validate-email-address-in-javascript
  return !!text
    .trim()
    .match(
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i,
    );
}

export function isValidPhone(text) {
  // From: https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript
  return !!text.trim().match(/\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})/g);
}

export const toDbPhone = (number) => {
  if (!number) return '';
  return number.replace(/[^0-9]+/g, '');
};

export function isImage(url) {
  if (!url) return false;
  return url.match(/\.(jpg|gif|png|jpeg)$/) != null;
}

export function isNaturalNumber(n) {
  n = n.toString();
  var n1 = Math.abs(n),
    n2 = parseInt(n, 10);
  return !isNaN(n1) && n2 === n1 && n1.toString() === n;
}

export function validatePhone(rule, value, callback) {
  if (value) {
    const isValid = isValidPhone(value);
    if (isValid) {
      callback();
    } else if (!value.startsWith('+') && value.length === 10) {
      callback();
    } else {
      callback(i18n.t('common:invalid-phone'));
    }
  } else {
    callback();
  }
}

export function isChecked(rule, value, callback) {
  if (value) {
    callback();
  } else {
    callback(rule.message);
  }
}

export function validateUrl(type) {
  return (rule, value, callback) => {
    const message =
      rule.message ||
      'Invalid URL, please include the protocol (i.e. https://) before the domain';
    if (value) {
      if (!isUrl(value)) {
        if (!value.startsWith('http')) {
          return callback(
            'Please include the protocol (i.e. https://) before the domain',
          );
        }
        return callback('Invalid URL');
      }
      let isValid = true;
      switch (type) {
        case 'fb':
          isValid = value.includes('facebook.com');
          break;
        case 'tw':
          isValid = value.includes('twitter.com');
          break;
        case 'ig':
          isValid = value.includes('instagram.com');
          break;
        case 'in':
          isValid = value.includes('linkedin.com');
          break;
        case 'yt':
          isValid = value.includes('youtube.com');
          break;
        default:
          isValid = isUrl(value);
      }
      if (isValid) {
        callback();
      } else {
        callback(message);
      }
    } else {
      callback();
    }
  };
}

export function toUserTimezone(dateStr, timezone, format) {
  const tz = timezone || 'America/Chicago';
  return moment.utc(dateStr, format).tz(tz);
}

export function toMoney(cents = 0) {
  const dollar = Number(cents) / 100;
  var formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(dollar);
}

export const timeAgo = (date) => {
  var seconds = Math.floor((new Date() - date) / 1000);

  var interval = Math.floor(seconds / 31536000);

  if (interval > 1) {
    return interval + ' years';
  }
  interval = Math.floor(seconds / 2592000);
  if (interval > 1) {
    return interval + ' months';
  }
  interval = Math.floor(seconds / 86400);
  if (interval > 1) {
    return interval + ' days';
  }
  interval = Math.floor(seconds / 3600);
  if (interval > 1) {
    return interval + ' hours';
  }
  interval = Math.floor(seconds / 60);
  if (interval > 1) {
    return interval + ' minutes';
  }
  return Math.floor(seconds) + ' seconds';
};

export const replaceLinkWithAnchor = (html) => {
  const text = html.replace(/&nbsp;/g, ' ');

  return anchorme({
    input: text,
    options: {
      attributes: [
        {
          name: 'target',
          value: '_blank',
        },
      ],
    },
  });
};

export function getOS() {
  if (global.IS_SERVER) return;
  var userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
}

export const getShortCode = (account) => {
  if (account.account_allow_shortcode_use === '0') {
    return '3';
  }
  return account.account_country === 'CA' ? '2' : '1';
};

export const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const prettyFileSize = function (bytes) {
  if (bytes == 0) {
    return '0.00 B';
  }
  var e = Math.floor(Math.log(bytes) / Math.log(1024));
  return (
    (bytes / Math.pow(1024, e)).toFixed(2) + ' ' + ' KMGTP'.charAt(e) + 'B'
  );
};

export const formatCurrency = (cents) => {
  const val = Number(cents);
  if (isFinite(val)) return '$' + (val / 100).toFixed(2);
  return cents;
};

export function stringAvatar(name = '', sx = {}) {
  const [one, two = ''] = name.split(' ');
  return {
    sx: {
      bgcolor: stringToColor(name),
      ...sx,
    },
    children: `${one[0]}${two[0] || ''}`.trim().toUpperCase(),
  };
}

export function html2text(html, removeLineBreaks = false) {
  if (html) {
    const doc = new DOMParser().parseFromString(
      html.replace(/<br>/g, '\n'),
      'text/html',
    );
    // Chrome treats &nbsp; with 160 instead of 32
    const text = (doc.body.textContent || '').replace(
      new RegExp(String.fromCharCode(160), 'g'),
      ' ',
    );
    if (removeLineBreaks) {
      return text.replace(/(\r\n|\n|\r)/gm, ' ').replace('&nbsp;', ' ');
    }
    return text;
  }
  return '';
}

export function shortId() {
  return (Math.random() * Math.random()).toString(36).substring(2, 15);
}

export function formatDuration(
  ms,
  options = {
    secondsDecimalDigits: 0,
    colonNotation: true,
  },
) {
  if (!ms || !isFinite(ms)) return '0s';
  return prettyMs(ms, options);
}

export const formatNumber = (number) => {
  return new Intl.NumberFormat().format(number);
};
